import React from "react";
import { IonIcon, IonItem } from "@ionic/react";
import _ from "lodash";
import { ProgramData, ProgramRole } from "@interfaces/Interfaces";
import { Button_Checkbox_Active, Button_Checkbox_Inactive, Icon_Radio_Active, Icon_Radio_Grey } from "@assets/images";
import * as ProgramUtils from "@utils/programUtils";
import classNames from "classnames";
import { informationCircle } from "ionicons/icons";
import { getMandatorySecondaryRoleNames } from "@utils/programDetails/programDetailsValidation";

interface RoleSelectionSectionProps {
  program: ProgramData;
  allRoles: ProgramRole[];
  selectedPrimaryRoles: string[];
  selectedSecondaryRoles: string[];
  onPrimaryRoleChange: (selectedPrimaryRoles: string[]) => void;
  onSecondaryRolesChange: (selectedSecondaryRoles: string[]) => void;
  onStandardsReset: () => void;
  onTeamReset: () => void;
  onAreaReset: () => void;
}

const RoleSelectionSection: React.FC<RoleSelectionSectionProps> = ({
  program,
  allRoles,
  selectedPrimaryRoles,
  selectedSecondaryRoles,
  onPrimaryRoleChange,
  onSecondaryRolesChange,
  onStandardsReset,
  onTeamReset,
  onAreaReset,
}) => {
  const getRoleIcon = (selected: boolean) => {
    if (selected) {
      if (ProgramUtils.useRadioRoleSelector(program?.ID)) {
        return Icon_Radio_Active;
      } else {
        return Button_Checkbox_Active;
      }
    } else {
      if (ProgramUtils.useRadioRoleSelector(program?.ID)) {
        return Icon_Radio_Grey;
      } else {
        return Button_Checkbox_Inactive;
      }
    }
  };

  const toggleValue = (value: string): void => {
    let array = _.cloneDeep(selectedPrimaryRoles);

    if (!Array.isArray(array)) {
      array = [array];
    }

    if (array.includes(value)) {
      array = array.filter((_item) => _item !== value);
    } else {
      if (ProgramUtils.useRadioRoleSelector(program?.ID) && array.length === 1) {
        array = [value];
      } else {
        array.push(value);
      }
    }

    onPrimaryRoleChange(array);

    if (ProgramUtils.isSPAPRP(program?.ID) || ProgramUtils.isNwasARC(program?.ID)) {
      onTeamReset();
      onAreaReset();
    }
    onStandardsReset();
    onSecondaryRolesChange(getMandatorySecondaryRoleNames(array, program));
  };

  const handleOptionalSecondaryRoleChange = (role: ProgramRole): void => {
    if (selectedSecondaryRoles.includes(role.Name)) {
      onSecondaryRolesChange(selectedSecondaryRoles.filter((name) => name !== role.Name));
    } else {
      onSecondaryRolesChange([...selectedSecondaryRoles, role.Name]);
    }
  };

  const selectablePrimaryRoles = allRoles.filter((role) => !role.AppearAsSecondaryRoleOnly);

  if (allRoles.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div
        className={classNames("editRolesHeader", {
          "mt-8": program?.Certificates,
          "mt-4": !program?.Certificates,
        })}
      >
        {ProgramUtils.getRolesLabel(program?.ID)}
      </div>
      {selectablePrimaryRoles.map((item: ProgramRole) => {
        const selected = selectedPrimaryRoles.includes(item.Name);

        return (
          <div key={item.ID}>
            <IonItem
              button
              detail={false}
              className="detailsRoleCheckboxContainer"
              onClick={() => toggleValue(item.Name)}
            >
              <div className="evidencePartsCheckboxContainerInner">
                <div className="programRoleCheckboxContainerInner">
                  <div className={`evidencePartsCheckboxLabel ${selected ? "evidencePartsCheckboxLabelSelected" : ""}`}>
                    {item.Name}
                  </div>
                  {item.Description && <div className="programRoleDescription">{item.Description}</div>}
                </div>
                <img
                  className="evidencePartsRadioCheckbox"
                  src={getRoleIcon(selected)}
                />
              </div>
            </IonItem>
            {selected && (
              <>
                <SecondaryRoleCard
                  roles={item.MandatorySecondaryRoles ?? []}
                  selectedSecondaryRoles={selectedSecondaryRoles}
                  readOnly
                  optional={false}
                />
                <SecondaryRoleCard
                  roles={item.OptionalSecondaryRoles ?? []}
                  selectedSecondaryRoles={selectedSecondaryRoles}
                  onSecondaryRoleChange={handleOptionalSecondaryRoleChange}
                  optional={true}
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface SecondaryRoleCardProps {
  roles: ProgramRole[];
  selectedSecondaryRoles: string[];
  onSecondaryRoleChange?: (role: ProgramRole) => void;
  readOnly?: boolean;
  optional: boolean;
}

const SecondaryRoleCard: React.FC<SecondaryRoleCardProps> = ({
  roles,
  selectedSecondaryRoles,
  onSecondaryRoleChange,
  readOnly = false,
  optional,
}) => {
  if (roles.length === 0) {
    return null;
  }

  return (
    <div className="mb-2 mt-1 rounded-lg border border-portfolio-secondary p-4">
      <div className="mb-3 text-[15px] font-bold leading-default tracking-[-0.2px] text-portfolio-secondary">
        {optional ? "Secondary role (Optional)" : "Secondary role (Mandatory)"}
      </div>
      {roles.map((role) => {
        const selected = readOnly || selectedSecondaryRoles.includes(role.Name);
        const rowContent = (
          <div className="evidencePartsCheckboxContainerInner w-full">
            <div className="programRoleCheckboxContainerInner">
              <div className={`evidencePartsCheckboxLabel ${selected ? "evidencePartsCheckboxLabelSelected" : ""}`}>
                {role.Name}
              </div>
            </div>
            <img
              alt=""
              className="evidencePartsRadioCheckbox"
              src={selected ? Icon_Radio_Active : Icon_Radio_Grey}
            />
          </div>
        );

        if (readOnly) {
          return (
            <div
              key={role.ID}
              className="flex min-h-[58px] w-full border-b border-grey-20 last:border-b-0"
            >
              {rowContent}
            </div>
          );
        }

        return (
          <button
            type="button"
            key={role.ID}
            className="flex min-h-[58px] w-full cursor-pointer border-0 border-b border-grey-20 p-0 text-left last:border-b-0"
            onClick={(event) => {
              event.stopPropagation();
              onSecondaryRoleChange?.(role);
            }}
          >
            {rowContent}
          </button>
        );
      })}
      <div className="mt-3 flex items-start gap-2">
        <IonIcon
          icon={informationCircle}
          className="mt-[1px] shrink-0 text-[18px] text-portfolio-secondary"
        />
        <div className="text-[13px] leading-[1.54] tracking-[-0.1px] text-portfolio-secondary">
          {"Secondary role is visible in your portfolio alongside your primary role."}
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionSection;
